'use client';

import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { Button, Input } from '@components/common/Forms';
import { CheckIcon } from '@heroicons/react/16/solid';
import { TailwindSpinner } from '@components/common';
import { showBuyNeuroModal } from '@components/features/modals/buy-neuro/BuyNeuroModal';
import { ActivationConfirmModal } from '@components/features/modals/ActivationConfirmModal';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { useSwrAccountDiscountData } from '@hooks/useSwrApi/endpoints/order/useSwrAccountDiscountData';
import { useSwrBestieScreeningActivate } from '@hooks/useSwrApi/endpoints/bestie-screening/useSwrBestieScreeningActivate';
import { useSwrGetSearchPackPackages } from '@hooks/useSwrApi/endpoints/user-searchpack/useSwrGetSearchPackPackages';
import { useSwrSearchPackActivatePackage } from '@hooks/useSwrApi/endpoints/user-searchpack/useSwrSearchPackActivatePackage';
import { useSwrBestieScreeningPackages } from '@hooks/useSwrApi/endpoints/bestie-screening/useSwrBestieScreeningPackages';
import { useCreditsCalculator } from '@hooks/credits/useCreditsCalculator';
import { useStaticToasts } from '@hooks/useStaticToasts/useStaticToasts';
import { useOfferPageServices } from './hooks/useOfferPageServices';

/** Formats a price/number string: extracts the numeric part and shows compact form (1k, 2k, etc.) when >= 1000 */
const formatCompactPrice = (value: string | number | undefined): string => {
  if (value === undefined || value === null) return '';
  const str = String(value);
  const match = str.match(/(\d[\d.,]*)/);
  if (!match) return str;
  const num = parseFloat(match[1].replace(/,/g, ''));
  if (Number.isNaN(num)) return str;
  if (num >= 1000 && num % 1000 === 0) {
    return str.replace(match[1], `${num / 1000}k`);
  }
  return str;
};

interface ModalConfig {
  packageType: string;
  creditTier: string;
  subtitle: string;
  onConfirm: () => void;
}

interface OfferPageServicesTableProps {
  isAuth: boolean;
  className?: string;
}

export const OfferPageServicesTable = (props: OfferPageServicesTableProps) => {
  const { isAuth, className = '' } = props;
  const { t } = useTranslation('common');
  const { router, locale } = useExtendedRouter();
  const { status200Toast } = useStaticToasts();

  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(null);
  const [selectedSourcingPack, setSelectedSourcingPack] = useState<string | null>(null);
  const [creditAmount, setCreditAmount] = useState<number>(1000);

  // Business data and phone validation hook
  const { validateAndActivate } = useOfferPageServices({ locale, isAuth });

  // Modal state
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [modalConfig, setModalConfig] = useState<ModalConfig | null>(null);
  // Fetch bestie screening packages
  const { data: packages, isLoading: packagesLoading } = useSwrBestieScreeningPackages();

  // Set the recommended package as selected by default when packages are loaded
  useEffect(() => {
    if (packages && packages.length > 0 && !selectedPackageId) {
      const recommendedPackage = packages.find((pack) => pack.recommended);
      const defaultPackage = recommendedPackage || packages[0];
      setSelectedPackageId(defaultPackage.packageId ?? null);
    }
  }, [packages, selectedPackageId]);

  // Account discount data for topup box (fetch for all users to show 0 values)
  const { data: accountDiscountData } = useSwrAccountDiscountData({ locale, fetchCondition: true });

  // Fetch search pack packages
  const { data: sourcingPackages, isLoading: isSourcingPackagesLoading } = useSwrGetSearchPackPackages({ locale });

  // Set the second sourcing pack as default when packages are loaded
  useEffect(() => {
    if (sourcingPackages && sourcingPackages.length > 0 && !selectedSourcingPack) {
      // Select second pack if available, otherwise first
      const defaultPack = sourcingPackages.length > 1 ? sourcingPackages[1] : sourcingPackages[0];
      setSelectedSourcingPack(defaultPack.identifier ?? null);
    }
  }, [sourcingPackages, selectedSourcingPack]);

  // Bestie screening activation hook
  const { trigger: triggerBestieActivate, isMutating: isBestieActivating } = useSwrBestieScreeningActivate({
    onSuccess: () => {
      status200Toast(t('flash.message.deal.success'));

      void router.push('/screening');
    },
  });

  // Sourcing pack activation hook
  const { trigger: triggerSourcingActivate, isMutating: isSourcingActivating } = useSwrSearchPackActivatePackage({});

  // Credits calculator for topup box
  const { priceData, priceIsValidating } = useCreditsCalculator(creditAmount);

  // Get the currently selected screening package data
  const currentScreeningPackData = packages?.find((p) => p.packageId === selectedPackageId);

  // Get the currently selected sourcing package data
  const currentSourcingPackData = sourcingPackages?.find((pkg) => pkg.identifier === selectedSourcingPack);

  // Calculate values for topup info display
  const lastYearValue = Math.round(priceData?.lastYearOrderValueEur ?? 0);
  const totalConsumption = creditAmount + lastYearValue;

  // Bestie screening price calculation
  const screeningFullPrice = currentScreeningPackData?.price ?? 0;
  const screeningDiscountedPrice: number = currentScreeningPackData?.discountedPrice ?? screeningFullPrice;
  const hasDiscount = screeningDiscountedPrice > 0 && screeningDiscountedPrice < screeningFullPrice;

  // Calculate the next discount tier based on totalConsumption using discountLevels
  // This always shows the next tier regardless of current discount level
  const discountLevels = priceData?.discountLevels ?? accountDiscountData?.discountLevels ?? [];
  const nextTier = discountLevels
    .filter((level) => (level.minAmount ?? 0) > totalConsumption)
    .sort((a, b) => (a.minAmount ?? 0) - (b.minAmount ?? 0))[0];

  const differenceToNextTier = nextTier ? Math.round((nextTier.minAmount ?? 0) - totalConsumption) : 0;
  const nextTierPercentage = nextTier?.percentage;
  const showNextTierSuggestion = nextTier && differenceToNextTier > 0;


  const handleActivateSourcing = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/company/offer?scrollTo=sourcing-section')}`, { withLocale: false });
      return;
    }
    const packType = selectedSourcingPack;
    if (!packType) return;

    setModalConfig({
      packageType: t('services.sourcing.title'),
      creditTier: formatCompactPrice(currentSourcingPackData?.fullPrice),
      subtitle: t('services.sourcing.subtitle'),
      onConfirm: () => {
        void triggerSourcingActivate({ packType: packType as 'pack_20' | 'pack_100' | 'pack_220' | 'pack_625' });
        setIsConfirmModalOpen(false);
      },
    });
    setIsConfirmModalOpen(true);
  };

  const handleTopUp = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/company/offer?scrollTo=wallet-bar')}`, { withLocale: false });
      return;
    }
    showBuyNeuroModal(creditAmount);
  };

  const handleBuyBestie = () => {
    if (!isAuth) {
      void router.push(`/login?targetPath=${encodeURIComponent('/company/offer')}`, { withLocale: false });
      return;
    }
    if (!selectedPackageId) return;

    setModalConfig({
      packageType: t('services.bestie.screening.title'),
      creditTier: formatCompactPrice(currentScreeningPackData?.price),
      subtitle: t('services.bestie.subtitle'),
      onConfirm: () => {
        // Validate business data and phone before activating
        validateAndActivate(() => {
          void triggerBestieActivate({
            packageId: selectedPackageId,
            paymentMethod: 'neuro',
          });
        });
        setIsConfirmModalOpen(false);
      },
    });
    setIsConfirmModalOpen(true);
  };

  return (
    <div className={`grid grid-cols-1 gap-4 lg:grid-cols-3 ${className}`}>
      {/* Sourcing Packs */}
      <div id="sourcing-section" className="relative flex h-full flex-col rounded-md border bg-surface p-5">
        {/* Title and pack selection - move to a new line when not enough space */}
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-left font-bold">
            {t('services.sourcing.title')}
          </h3>

          {/* Pack selection buttons */}
          <div className="flex flex-wrap gap-1">
            {isSourcingPackagesLoading ? (
              <TailwindSpinner className="size-5 text-ink-medium" />
            ) : (
              (sourcingPackages ?? []).map((pkg) => (
                <button
                  key={pkg.identifier}
                  type="button"
                  onClick={() => setSelectedSourcingPack(pkg.identifier ?? null)}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                    selectedSourcingPack === pkg.identifier
                      ? 'bg-secondary text-white'
                      : 'bg-surface-100 text-ink hover:bg-surface-200'
                  }`}
                >
                  {formatCompactPrice(pkg.fullPrice)}
                </button>
              ))
            )}
          </div>
        </div>

        <p className="text-sm text-ink-medium">
          {t('services.sourcing.subtitle')}
        </p>

        {/* Benefits list */}
        <div className="mt-3 flex-1">
          <div className="flex flex-col gap-2 py-1 text-left text-sm">
            <div className="flex items-start gap-2">
              <CheckIcon className="size-5 min-w-5 text-secondary" />
              <span>{t('services.sourcing.benefit1')}</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckIcon className="size-5 min-w-5 text-secondary" />
              <span>{t('services.sourcing.benefit2')}</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckIcon className="size-5 min-w-5 text-secondary" />
              <span>{t('services.sourcing.benefit3')}</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckIcon className="size-5 min-w-5 text-secondary" />
              <span>
                <Trans
                  i18nKey="services.sourcing.benefit4"
                  values={{ credits: currentSourcingPackData?.unlocks ?? '' }}
                />
              </span>
            </div>
          </div>
        </div>

        {/* Price display */}
        <div className="mt-5 flex items-baseline gap-2">
          {isSourcingPackagesLoading ? (
            <TailwindSpinner className="size-8 text-ink-medium" />
          ) : currentSourcingPackData && (
            <>
              {currentSourcingPackData.discountedPrice && currentSourcingPackData.discountedPrice !== '...' && currentSourcingPackData.discountedPrice !== currentSourcingPackData.fullPrice ? (
                <>
                  <span className="text-3xl font-bold md:text-4xl">
                    {currentSourcingPackData.discountedPrice}
                  </span>
                  <span className="text-lg text-ink-medium line-through">
                    {currentSourcingPackData.fullPrice}
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold md:text-4xl">
                  {currentSourcingPackData.fullPrice}
                </span>
              )}
              <span className="text-sm text-ink-medium">
                {t('employer.branding.price.desc')}
              </span>
            </>
          )}
        </div>

        {/* Activate button */}
        <Button
          styling="solid"
          color="secondary"
          rounding="full"
          size="sm"
          className="mt-3 w-fit"
          isLoading={isSourcingActivating || isSourcingPackagesLoading}
          onClick={handleActivateSourcing}
          disabled={!currentSourcingPackData}
        >
          {t('offer.subscription_section.label.activate')}
        </Button>
      </div>

      {/* Bestie.jobs with pack selection */}
      <div className="relative flex h-full flex-col rounded-md border bg-surface p-5">
        {/* Title and pack selection - move to a new line when not enough space */}
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-left font-bold">
            {t('services.bestie.screening.title')}
          </h3>

          {/* Pack selection buttons */}
          <div className="flex flex-wrap gap-1">
            {packagesLoading ? (
              <TailwindSpinner className="size-5 text-ink-medium" />
            ) : (
              packages?.map((pack) => (
                <button
                  key={pack.packageId}
                  type="button"
                  onClick={() => setSelectedPackageId(pack.packageId ?? null)}
                  className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                    selectedPackageId === pack.packageId
                      ? 'bg-secondary text-white'
                      : 'bg-surface-100 text-ink hover:bg-surface-200'
                  }`}
                >
                  {formatCompactPrice(pack.price)}
                </button>
              ))
            )}
          </div>
        </div>

        <p className="text-sm text-ink-medium">
          {t('services.bestie.subtitle')}
        </p>

        {/* Benefits list */}
        <div className="mt-3 flex-1">
          <div className="flex flex-col gap-2 py-1 text-left text-sm">
            <div className="flex items-start gap-2">
              <CheckIcon className="size-5 min-w-5 text-secondary" />
              <span>{t('services.bestie.jobs.line1')}</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckIcon className="size-5 min-w-5 text-secondary" />
              <span>{t('services.bestie.jobs.line2')}</span>
            </div>
            <div className="flex items-start gap-2">
              <CheckIcon className="size-5 min-w-5 text-secondary" />
              <span>
                <Trans
                  i18nKey="services.bestie.jobs.line3"
                  values={{
                    screeningCredits: packages?.find((p) => p.packageId === selectedPackageId)?.credits ?? 0,
                  }}
                />
              </span>
            </div>
          </div>
        </div>

        {/* Price display */}
        <div className="mt-5 flex items-baseline gap-2">
          {packagesLoading ? (
            <TailwindSpinner className="size-8 text-ink-medium" />
          ) : (
            <>
              {hasDiscount ? (
                <>
                  <span className="text-3xl font-bold md:text-4xl">
                    {screeningDiscountedPrice}
                  </span>
                  <span className="text-lg text-ink-medium line-through">
                    {screeningFullPrice}
                  </span>
                </>
              ) : (
                <span className="text-3xl font-bold md:text-4xl">
                  {screeningFullPrice}
                </span>
              )}
              <span className="text-sm text-ink-medium">
                {t('employer.branding.price.desc')}
              </span>
            </>
          )}
        </div>

        {/* Buy button */}
        <Button
          styling="solid"
          color="secondary"
          rounding="full"
          size="sm"
          className="mt-3 w-fit"
          isLoading={isBestieActivating}
          onClick={handleBuyBestie}
          disabled={!selectedPackageId || packagesLoading}
        >
          {t('offer.subscription_section.label.activate')}
        </Button>
      </div>

      {/* Wallet Top Up - Enhanced */}
      <div className="relative flex h-full flex-col rounded-md border bg-surface p-5">
        <h3 className="mb-3 text-left font-bold">
          {`${t('wallet.top-up.order')} / Premium`}
        </h3>

        {/* Order neuro input */}
        <div className="mb-4">
          <Input
            id="topUpAmountTable"
            type="number"
            value={creditAmount || ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const val = e.target.value;
              if (val === '' || val === null) {
                setCreditAmount(0);
                return;
              }
              const numVal = parseInt(val, 10);
              if (!Number.isNaN(numVal)) {
                setCreditAmount(numVal);
              }
            }}
            className="w-full"
          />
          <span className="mt-1 block text-xs text-ink-medium">
            {t('neuro.label.order-amount-no-discount')}
          </span>
          {showNextTierSuggestion && (
            <button
              type="button"
              onClick={() => setCreditAmount(creditAmount + differenceToNextTier)}
              className="mt-1 text-left text-xs text-secondary hover:underline"
            >
              <Trans
                i18nKey="page.offer-configurator.supplement.desc"
                values={{
                  nrOfCredits: differenceToNextTier,
                  nrPercentageDiscount: nextTierPercentage,
                }}
              />
            </button>
          )}
        </div>

        <div className="mb-4 rounded-md bg-surface-100 p-2 text-sm">
          <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
            <div>{t('price.calculator.euro.buy.history')}</div>
            <div>
              {priceIsValidating
                ? <TailwindSpinner className="size-4 text-ink-medium" />
                : lastYearValue}
            </div>
          </div>

          <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
            <div>{t('price.calculator.euro.buy.total')}</div>
            <div>
              {priceIsValidating
                ? <TailwindSpinner className="size-4 text-ink-medium" />
                : totalConsumption}
            </div>
          </div>

          <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
            <div>{t('price.calculator.euro.current.discount')}</div>
            <div>
              {priceIsValidating
                ? <TailwindSpinner className="size-4 text-ink-medium" />
                : (
                  <>
                    {priceData?.currentDiscountPercentage ?? 0}
                    %
                  </>
                )}
            </div>
          </div>

          <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
            <div>{t('price.calculator.euro.new.discount')}</div>
            <div>
              {priceIsValidating
                ? <TailwindSpinner className="size-4 text-ink-medium" />
                : (
                  <>
                    {priceData?.nextDiscountPercentage ?? 0}
                    %
                  </>
                )}
            </div>
          </div>

          <div className="flex items-baseline justify-between gap-2 py-1 font-thin">
            <div>{t('price.calculator.new.discount.available')}</div>
            <div className="whitespace-nowrap text-right">
              {priceIsValidating
                ? <TailwindSpinner className="size-4 text-ink-medium" />
                : (priceData?.currentDiscountLevelExpirationDate ?? '-')}
            </div>
          </div>

        </div>

        <div className="mt-auto grid grid-cols-2 items-baseline gap-2 pt-5">
          <span className="text-sm text-ink-medium">{t('page.offer.calculator.total_net.euro')}</span>
          <div className="text-right text-2xl font-bold text-ink">
            {priceIsValidating
              ? <TailwindSpinner className="ml-auto size-6 text-ink-medium" />
              : (
                <>
                  {priceData?.priceToPay ?? creditAmount}
                  {' '}
                  €
                </>
              )}
          </div>
        </div>

        {/* Buy button */}
        <div className="mt-3">
          <Button
            onClick={handleTopUp}
            rounding="full"
            color="secondary"
            size="sm"
            className="w-fit"
          >
            {t('global.buy-neuro')}
          </Button>
        </div>
      </div>

      {modalConfig && (
        <ActivationConfirmModal
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          onConfirm={modalConfig.onConfirm}
          packageType={modalConfig.packageType}
          creditTier={modalConfig.creditTier}
          subtitle={modalConfig.subtitle}
          isLoading={isSourcingActivating || isBestieActivating}
        />
      )}
    </div>
  );
};
